import sanitizeHtml from 'sanitize-html'
import { isString } from '../common/utils/type-guards.js'

/**
 * Remove undefined values from an object recursively
 * Firestore doesn't accept undefined values in document writes
 */
export function removeUndefinedFields<T>(data: T): T {
  if (data === null || data === undefined) {
    return data
  }

  if (data instanceof Date) {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(removeUndefinedFields).filter(item => item !== undefined) as unknown as T
  }

  if (typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (value !== undefined) {
        result[key] = (typeof value === 'object' && value !== null) ? removeUndefinedFields(value) : value
      }
    }
    return result as T
  }

  return data
}

/**
 * Safely parse JSON string, return null if parsing fails
 * Use this for Redis cached data to prevent crashes from corrupted cache
 */
export function safeJsonParse<T>(jsonString: string | null): T | null {
  if (!jsonString) {
    return null
  }

  try {
    return JSON.parse(jsonString) as T
  } catch {
    return null
  }
}

/**
 * Sanitize user input to prevent XSS attacks
 * Removes all HTML tags and dangerous characters
 */
export function sanitizeInput(input: string | undefined): string {
  if (!input) return ''

  // Remove all HTML tags and scripts
  return sanitizeHtml(input, {
    allowedTags: [], // No HTML tags allowed
    allowedAttributes: {},
    disallowedTagsMode: 'recursiveEscape'
  }).trim()
}

/**
 * Sanitize array of strings
 */
export function sanitizeArray(arr: string[] | undefined): string[] {
  if (!arr || !Array.isArray(arr)) return []
  return arr.map(sanitizeInput).filter(Boolean)
}

/**
 * Sanitize plan data before saving
 */
export function sanitizePlanData<T extends Record<string, any>>(data: T): T {
  const result = { ...data } as any
  if ('name' in result && isString(result.name)) {
    result.name = sanitizeInput(result.name)
  }
  if ('description' in result && isString(result.description)) {
    result.description = sanitizeInput(result.description)
  }
  if ('tags' in result && Array.isArray(result.tags)) {
    result.tags = sanitizeArray(result.tags)
  }
  return result as T
}

/**
 * Sanitize stage data before saving
 * Handles nested structures like practicalInfo, accommodation, activities
 */
export function sanitizeStageData<T extends Record<string, any>>(data: T): T {
  const result = { ...data } as any

  // Top-level text fields
  if ('name' in result && isString(result.name)) {
    result.name = sanitizeInput(result.name)
  }
  if ('notes' in result && isString(result.notes)) {
    result.notes = sanitizeInput(result.notes)
  }
  if ('icon' in result && isString(result.icon)) {
    result.icon = sanitizeInput(result.icon)
  }

  // Accommodation nested object
  if (result.accommodation && typeof result.accommodation === 'object') {
    if (result.accommodation.name) {
      result.accommodation.name = sanitizeInput(result.accommodation.name)
    }
    if (result.accommodation.address) {
      result.accommodation.address = sanitizeInput(result.accommodation.address)
    }
  }

  // Activities array
  if (Array.isArray(result.activities)) {
    result.activities = result.activities.map((activity: any) => ({
      ...activity,
      name: activity.name ? sanitizeInput(activity.name) : activity.name,
      category: activity.category ? sanitizeInput(activity.category) : activity.category
    }))
  }

  // Practical info categories
  if (result.practicalInfo && typeof result.practicalInfo === 'object') {
    if (Array.isArray(result.practicalInfo.categories)) {
      result.practicalInfo.categories = result.practicalInfo.categories.map((cat: any) => ({
        ...cat,
        value: cat.value ? sanitizeInput(cat.value) : cat.value,
        details: cat.details ? {
          ...cat.details,
          type: cat.details.type ? sanitizeInput(cat.details.type) : cat.details.type,
          notes: cat.details.notes ? sanitizeInput(cat.details.notes) : cat.details.notes
        } : cat.details
      }))
    }
    if (Array.isArray(result.practicalInfo.tags)) {
      result.practicalInfo.tags = sanitizeArray(result.practicalInfo.tags)
    }
  }

  // POIs array
  if (Array.isArray(result.pois)) {
    result.pois = result.pois.map((poi: any) => ({
      ...poi,
      name: poi.name ? sanitizeInput(poi.name) : poi.name,
      address: poi.address ? sanitizeInput(poi.address) : poi.address
    }))
  }

  // Photos captions
  if (Array.isArray(result.photos)) {
    result.photos = result.photos.map((photo: any) => ({
      ...photo,
      caption: photo.caption ? sanitizeInput(photo.caption) : photo.caption
    }))
  }

  // Location address
  if (result.location && typeof result.location === 'object') {
    if (result.location.address) {
      result.location.address = sanitizeInput(result.location.address)
    }
    if (result.location.name) {
      result.location.name = sanitizeInput(result.location.name)
    }
  }

  return result as T
}
